import { useState } from "react";
import { useApolloClient } from "@apollo/client";
import { v4 as uuidv4 } from "uuid";
import { ObjectSchema, object as objectSchema, ValidationError } from "yup";
import { capitalize, flatten, keyBy, omit } from "lodash";
import type { DeepPartial, FieldPath } from "react-hook-form";
import { DocumentType, gql } from "__generated__/gql";
import { DocumentVariablesType } from "utils/apollo/hooks";
import {
  ValidatedData as GetValidatedData,
  ValidColumnKeysBySheet,
} from "utils/xlsxImportExport/XlsxImportWorkBook";
import * as productLineSchemaFields from "utils/schemas/productLine";
import * as skuSchemaFields from "utils/schemas/sku";
import * as skuComponentSchemaFields from "utils/schemas/skuComponent";
import * as dimensionsSchemaFields from "utils/schemas/dimensions";
import * as tiHiSchemaFields from "utils/schemas/tiHi";
import createDefinitionHelpers from "utils/xlsxImportExport/definitionHelpers";
import {
  DimensionInput,
  ProductLineInput,
  SkuComponentInput,
  SkuComponentType,
  SkuFilter,
  SkuInput,
  TiHiInput,
  WeightUnit,
} from "__generated__/gql/graphql";
import getChunkedTuple from "utils/getChunkedTuple";
import { DEFAULT_MAX_BYTE_SIZE } from "utils/misc";
import {
  makeClearListFieldCache,
  makeClearNestedListFieldCache,
  makeClearCachedType,
} from "utils/apollo/cacheHelpers";
import { useBrandQuery } from "utils/apollo/hooks/brand";
import { WEIGHT_UNIT_BY_LABEL } from "components/Shared/weightUnit";
import { useCurrentBrandId } from "utils/currentUserHooks";
import { assertPresence } from "utils/assertions";
import {
  SKU_COMPONENT_TYPE_LABELS,
  SKU_COMPONENT_TYPES,
} from "components/Products/SkuPanel/SkuComponents/utils";
import { DIMENSIONS_FIELDS } from "components/Shared/Forms/DimensionsFieldset";
import { ColumnDefinition } from "utils/xlsxImportExport/XlsxImportColumn";

export type ProductLineRow = {
  name: string;
  shortName?: string | null;
  nameSeparator?: string;
  shipsFromZip: string | null;
  innerCaseQuantity: number | null;
  wholesaleCaseQuantity: number | null;
  wholesaleUnitPrice: string | null;
};

export const skuComponentFieldPrefixByType = SKU_COMPONENT_TYPES.reduce(
  (acc, sct) => {
    acc[sct] = sct.toLocaleLowerCase() as Lowercase<typeof sct>;
    return acc;
  },
  {} as Record<SkuComponentType, Lowercase<SkuComponentType>>,
);
export const skuComponentFieldPrefixes = SKU_COMPONENT_TYPES.map(
  (sct) => skuComponentFieldPrefixByType[sct],
);
export const skuComponentUpcFields = skuComponentFieldPrefixes.map(
  (p) => `${p}Upc` as const,
);
export const skuComponentGrossWeightFields = skuComponentFieldPrefixes.map(
  (p) => `${p}GrossWeightLbs` as const,
);
const DIMENSIONS_FIELDS_CAPITALIZED = DIMENSIONS_FIELDS.map(
  (f) => capitalize(f) as Capitalize<typeof f>,
);
export const skuComponentDimensionFields = flatten(
  skuComponentFieldPrefixes.map((p) =>
    DIMENSIONS_FIELDS_CAPITALIZED.map((f) => `${p}Dimension${f}` as const),
  ),
);
export const skuComponentFields = [
  ...skuComponentUpcFields,
  ...skuComponentGrossWeightFields,
  ...skuComponentDimensionFields,
];

type SkuComponentTypePrefixes = (typeof skuComponentFieldPrefixes)[number];
export type SkuComponentUpcFields = (typeof skuComponentUpcFields)[number];
export type SkuComponentGrossWeightFields =
  (typeof skuComponentGrossWeightFields)[number];
export type SkuComponentDimensionFields =
  (typeof skuComponentDimensionFields)[number];
export type SkuComponentFields = (typeof skuComponentFields)[number];

type SkuRowComponentFields = Partial<Record<SkuComponentFields, string | null>>;

export type SkuRow = {
  productLineName: string;
  name: string;
  shortName?: string | null;
  internalSkuId?: string | null;
  unitNetWeight?: string | null;
  unitNetWeightUnit?: WeightUnit | null;
  unitCogs?: string | null;
  unitSrp?: string | null;
  ti?: number | null;
  hi?: number | null;
  grossPalletWeightLbs?: string | null;
} & SkuRowComponentFields;

const productLineSchema = objectSchema<ProductLineRow>().shape({
  name: productLineSchemaFields.name,
  shortName: productLineSchemaFields.shortName,
  nameSeparator: productLineSchemaFields.nameSeparator,
  shipsFromZip: productLineSchemaFields.shipsFromZip.default(null),
  innerCaseQuantity: productLineSchemaFields.innerCaseQuantity.default(null),
  wholesaleCaseQuantity:
    productLineSchemaFields.wholesaleCaseQuantity.default(null),
  wholesaleUnitPrice: productLineSchemaFields.wholesaleUnitPrice.default(null),
});

const productLineDef = createDefinitionHelpers<ProductLineRow>();

export const PRODUCT_LINE_WORKSHEET_DEFINITION = productLineDef.workSheet({
  key: "productLines",
  sheetName: "Product Lines",
  schema: productLineSchema,
  columnDefinitions: [
    { key: "name", header: "Name", isRequired: true },
    { key: "shortName", header: "Name (Short)" },
    { key: "nameSeparator", header: "Name Separator" },
    { key: "shipsFromZip", header: "Ships From Zip" },
    { key: "innerCaseQuantity", header: "Inner Case Quantity" },
    { key: "wholesaleCaseQuantity", header: "Wholesale Case Quantity" },
    { key: "wholesaleUnitPrice", header: "Wholesale Unit Price" },
  ],
});

const skuDef = createDefinitionHelpers<SkuRow>();

const skuComponentTypeSchema = objectSchema().shape({
  upc: skuComponentSchemaFields.upc,
  grossWeightLbs: skuComponentSchemaFields.grossWeightLbs,
  dimensions: skuComponentSchemaFields.dimensions,
});

const skuSubSchema = objectSchema().shape({
  ...SKU_COMPONENT_TYPES.reduce(
    (acc, sct) => {
      acc[`${skuComponentFieldPrefixByType[sct]}SkuComponent`] =
        skuComponentTypeSchema;
      return acc;
    },
    {} as Record<
      `${SkuComponentTypePrefixes}SkuComponent`,
      typeof skuComponentTypeSchema
    >,
  ),
  tiHi: skuSchemaFields.tiHi,
});

type ObjectSchemaType<T> = T extends ObjectSchema<infer R> ? R : never;

type SkuSubSchema = ObjectSchemaType<typeof skuSubSchema>;

const skuSubSchemaPathMap = {
  ...SKU_COMPONENT_TYPES.reduce(
    (acc, sct) => {
      const prefix = skuComponentFieldPrefixByType[sct];
      acc[`${prefix}SkuComponent.upc`] = `${prefix}Upc`;
      DIMENSIONS_FIELDS_CAPITALIZED.forEach((f) => {
        const dimLower = f.toLowerCase() as Lowercase<typeof f>;
        acc[`${prefix}SkuComponent.dimensions.${dimLower}`] =
          `${prefix}Dimension${f}`;
      });
      acc[`${prefix}SkuComponent.grossWeightLbs`] = `${prefix}GrossWeightLbs`;
      return acc;
    },
    {} as Record<FieldPath<SkuSubSchema>, keyof SkuRow>,
  ),
  "tiHi.ti": "ti",
  "tiHi.hi": "hi",
};

function getSkuSubSchemaData(row: Partial<SkuRow>) {
  const skuComponents = SKU_COMPONENT_TYPES.reduce(
    (acc, sct) => {
      const prefix = skuComponentFieldPrefixByType[sct];
      acc[`${prefix}SkuComponent`] = {
        upc: row[`${prefix}Upc`],
        dimensions: DIMENSIONS_FIELDS_CAPITALIZED.reduce(
          (dimAcc, f) => {
            const dimLower = f.toLowerCase() as Lowercase<typeof f>;
            // eslint-disable-next-line no-param-reassign
            dimAcc[dimLower] = row[`${prefix}Dimension${f}`] || undefined;
            return dimAcc;
          },
          {} as Record<
            Lowercase<(typeof DIMENSIONS_FIELDS)[number]>,
            string | undefined
          >,
        ),
        grossWeightLbs: row[`${prefix}GrossWeightLbs`],
      };
      return acc;
    },
    {} as Record<
      `${SkuComponentTypePrefixes}SkuComponent`,
      DeepPartial<ObjectSchemaType<typeof skuComponentTypeSchema>>
    >,
  );

  return {
    ...skuComponents,
    tiHi: { ti: row.ti, hi: row.hi },
  };
}

const heightDimensionSchema = dimensionsSchemaFields.height
  .default(null)
  .notRequired();

const skuSchema = objectSchema()
  .shape({
    productLineName: productLineSchemaFields.name,
    name: skuSchemaFields.name,
    shortName: skuSchemaFields.shortName,
    internalSkuId: skuSchemaFields.internalSkuId.default(null),
    ...skuComponentUpcFields.reduce(
      (acc, f) => {
        acc[f] = skuComponentSchemaFields.upc.default(null);
        return acc;
      },
      {} as Record<SkuComponentUpcFields, typeof skuComponentSchemaFields.upc>,
    ),
    unitNetWeight: skuSchemaFields.unitNetWeight.default(null),
    unitNetWeightUnit: skuSchemaFields.unitNetWeightUnit
      .default(null)
      .transform((v: string) => (v && WEIGHT_UNIT_BY_LABEL[v]) || v),
    unitCogs: skuSchemaFields.unitCogs.default(null),
    unitSrp: skuSchemaFields.unitSrp.default(null),
    ...SKU_COMPONENT_TYPES.reduce(
      (acc, sct) => {
        acc[`${skuComponentFieldPrefixByType[sct]}DimensionHeight`] =
          dimensionsSchemaFields.height.default(null).notRequired();
        acc[`${skuComponentFieldPrefixByType[sct]}DimensionWidth`] =
          dimensionsSchemaFields.width.default(null).notRequired();
        acc[`${skuComponentFieldPrefixByType[sct]}DimensionDepth`] =
          dimensionsSchemaFields.depth.default(null).notRequired();
        return acc;
      },
      {} as Record<SkuComponentDimensionFields, typeof heightDimensionSchema>,
    ),
    ...skuComponentGrossWeightFields.reduce(
      (acc, f) => {
        acc[f] = skuComponentSchemaFields.grossWeightLbs.default(null);
        return acc;
      },
      {} as Record<
        SkuComponentGrossWeightFields,
        typeof skuComponentSchemaFields.grossWeightLbs
      >,
    ),
    ti: tiHiSchemaFields.ti.default(null).notRequired(),
    hi: tiHiSchemaFields.hi.default(null).notRequired(),
    grossPalletWeightLbs: skuSchemaFields.grossPalletWeightLbs.default(null),
  })
  .test(async (obj) => {
    try {
      await skuSubSchema.validate(getSkuSubSchemaData(obj));
    } catch (e) {
      if (!(e instanceof ValidationError)) throw e;
      e.path =
        e.path &&
        skuSubSchemaPathMap[e.path as keyof typeof skuSubSchemaPathMap];
      throw e;
    }

    return true;
  });

export const SKU_WORKSHEET_DEFINITION = skuDef.workSheet({
  key: "skus",
  sheetName: "SKUs",
  schema: skuSchema,
  columnDefinitions: [
    { key: "productLineName", header: "Product Line Name", isRequired: true },
    { key: "name", header: "Name", isRequired: true },
    { key: "shortName", header: "Name (Short)" },
    { key: "internalSkuId", header: "Internal SKU Identifier" },
    ...SKU_COMPONENT_TYPES.map((sct) => ({
      key: `${skuComponentFieldPrefixByType[sct]}Upc` as const,
      header: `${SKU_COMPONENT_TYPE_LABELS[sct]} UPC`,
    })),
    { key: "unitNetWeight", header: "Unit Net Weight" },
    { key: "unitNetWeightUnit", header: "Unit Net Weight (Unit)" },
    { key: "unitCogs", header: "Unit COGS" },
    { key: "unitSrp", header: "Unit SRP" },
    ...SKU_COMPONENT_TYPES.reduce(
      (acc, sct) => {
        DIMENSIONS_FIELDS_CAPITALIZED.forEach((f) => {
          acc.push({
            key: `${skuComponentFieldPrefixByType[sct]}Dimension${f}` as const,
            header: `${SKU_COMPONENT_TYPE_LABELS[sct]} Dimensions – ${f} (in)`,
          });
        });
        return acc;
      },
      [] as ColumnDefinition<
        Partial<Record<SkuComponentDimensionFields, string | null>>
      >[],
    ),
    { key: "ti", header: "Ti" },
    { key: "hi", header: "Hi" },
    ...SKU_COMPONENT_TYPES.map((sct) => ({
      key: `${skuComponentFieldPrefixByType[sct]}GrossWeightLbs` as const,
      header: `Gross ${SKU_COMPONENT_TYPE_LABELS[sct]} Weight (lb)`,
    })),
    { key: "grossPalletWeightLbs", header: "Gross Pallet Weight (lb)" },
  ],
});

export const WORKSHEET_DEFINITIONS = [
  PRODUCT_LINE_WORKSHEET_DEFINITION,
  SKU_WORKSHEET_DEFINITION,
];

export type WorkSheetDefinitions = (typeof WORKSHEET_DEFINITIONS)[number];

export type ValidatedData = GetValidatedData<WorkSheetDefinitions>;

export type ValidColumnKeys = ValidColumnKeysBySheet<WorkSheetDefinitions>;

function getProductLineNames(data: ValidatedData) {
  const names: string[] = [];

  data.productLines.forEach((pl) => {
    if (!names.includes(pl.name)) {
      names.push(pl.name);
    }
  });

  data.skus.forEach((sku) => {
    if (!names.includes(sku.productLineName)) {
      names.push(sku.productLineName);
    }
  });

  return names;
}

export function getExistingSkusVariables(data: ValidatedData) {
  return data.skus.map<SkuFilter>((sku) => ({
    productLine: { name: { equalTo: sku.productLineName } },
    name: { equalTo: sku.name },
  }));
}

export const existingQuery = gql(/* GraphQL */ `
  query ProductsImportExisting($brandId: UUID!) {
    productLinesList(filter: { brandId: { equalTo: $brandId } }) {
      id
      name
    }
    skusList(filter: { productLine: { brandId: { equalTo: $brandId } } }) {
      id
      productLineId
      name
      tiHiId
      skuComponentsList {
        id
        skuComponentType
        dimensionsId
      }
    }
  }
`);

type ExistingData = DocumentType<typeof existingQuery>;

export function useLoadExisting(
  validatedData: ValidatedData | null | undefined,
) {
  const brandId = useCurrentBrandId();

  const { data, loading } = useBrandQuery(existingQuery, {
    skip: !brandId || !validatedData,
    variables: brandId ? { brandId } : undefined,
    fetchPolicy: "network-only",
    nextFetchPolicy: "standby",
  });

  if (!validatedData) return;

  const productLineNames = getProductLineNames(validatedData);

  const existingProductLines = data?.productLinesList?.filter((pl) =>
    productLineNames.includes(pl.name),
  );

  const productLinesById = keyBy(data?.productLinesList, "id");

  const importSkuNamesByProductLineName = validatedData.skus.reduce(
    (acc, sku) => {
      if (!acc[sku.productLineName]) acc[sku.productLineName] = [];
      acc[sku.productLineName].push(sku.name);
      return acc;
    },
    {} as { [productLine: string]: string[] },
  );

  const existingSkus = data?.skusList?.filter((sku) => {
    const pl = productLinesById[sku.productLineId];
    assertPresence(pl);
    const productLineSkuNames = importSkuNamesByProductLineName[pl.name];
    return productLineSkuNames?.includes(sku.name);
  });

  return {
    data,
    productLineNames,
    existingProductLines,
    existingSkus,
    isLoading: loading,
  };
}

export const importMutation = gql(/* GraphQL */ `
  mutation ImportProducts(
    $productLines: [ProductLineInput!] = []
    $productLinesForSkus: [ProductLineInput!] = []
    $dimensions: [DimensionInput!] = []
    $tiHis: [TiHiInput!] = []
    $skus: [SkuInput!] = []
    $skuComponents: [SkuComponentInput!] = []
  ) {
    upsertProductLinesByBrandIdAndName(input: { productLines: $productLines }) {
      productLines {
        id
        name
      }
    }
    upsertProductLinesForSkus: upsertProductLinesByBrandIdAndName(
      input: { productLines: $productLinesForSkus }
    ) {
      productLines {
        id
        name
      }
    }
    ...UpdateSkuO2O
    upsertSkusById(input: { skus: $skus }) {
      skus {
        id
        productLineId
        name
      }
    }
    upsertSkuComponentsBySkuIdAndSkuComponentType(
      input: { skuComponents: $skuComponents }
    ) {
      skuComponents {
        id
      }
    }
  }
`);

type ImportProductsData = DocumentType<typeof importMutation>;
type ImportProductsVariables = DocumentVariablesType<typeof importMutation>;

export function getDataToImport(
  data: ValidatedData,
  columnKeys: ValidColumnKeys,
  existingData: ExistingData,
  brandId: UUID,
): ImportProductsVariables {
  const existingProductLineIdsByName = existingData.productLinesList?.reduce<{
    [name: string]: UUID;
  }>((acc, pl) => {
    acc[pl.name] = pl.id;
    return acc;
  }, {});

  const existingSkusByProductLineIdBySkuName = existingData.skusList?.reduce<{
    [productLineId: UUID]: {
      [skuName: string]: NonNullable<ExistingData["skusList"]>[number];
    };
  }>((acc, sku) => {
    if (!acc[sku.productLineId]) acc[sku.productLineId] = {};
    acc[sku.productLineId][sku.name] = sku;
    return acc;
  }, {});

  const productLineIdsByName = {
    ...data.productLines.reduce<{ [name: string]: UUID }>((acc, pl) => {
      acc[pl.name] = existingProductLineIdsByName?.[pl.name] ?? uuidv4();
      return acc;
    }, {}),
    ...existingProductLineIdsByName,
  };

  const skuComponents: SkuComponentInput[] = [];
  const dimensions: DimensionInput[] = [];
  const tiHis: TiHiInput[] = [];

  const skus = data.skus.map<SkuInput>((sku) => {
    const skuWithoutSkuComponentFields = omit(sku, skuComponentFields);

    const { productLineName, ti, hi, ...rest } = skuWithoutSkuComponentFields;

    let productLineId = productLineIdsByName?.[productLineName];

    if (!productLineId) {
      productLineId = uuidv4();
      productLineIdsByName[productLineName] = productLineId;
    }

    const existingSku =
      existingSkusByProductLineIdBySkuName?.[productLineId]?.[rest.name];
    const existingSkuComponentsByType = keyBy(
      existingSku?.skuComponentsList,
      "skuComponentType",
    );

    const skuId = existingSku?.id || uuidv4();

    const subObjs = skuSubSchema.validateSync(getSkuSubSchemaData(sku));

    SKU_COMPONENT_TYPES.forEach((sct) => {
      const existingSkuComponent = existingSkuComponentsByType?.[sct];
      const prefix = skuComponentFieldPrefixByType[sct];
      const skuComponentObj = subObjs[`${prefix}SkuComponent`];
      const { dimensions: dimensionObj, ...skuComponentAttrs } =
        skuComponentObj;

      const dimension = dimensionObj
        ? {
            ...dimensionObj,
            id: existingSkuComponent?.dimensionsId || uuidv4(),
          }
        : null;

      if (dimension) dimensions.push(dimension);

      skuComponents.push({
        ...skuComponentAttrs,
        skuId,
        skuComponentType: sct,
        dimensionsId:
          dimension?.id ||
          (columnKeys.skus.includes(`${prefix}DimensionHeight`)
            ? null
            : undefined),
      });
    });

    const tiHi = subObjs.tiHi
      ? {
          ...subObjs.tiHi,
          id: existingSku?.tiHiId || uuidv4(),
        }
      : null;

    if (tiHi) tiHis.push(tiHi);

    return {
      id: skuId,
      productLineId,
      ...rest,
      tiHiId: tiHi?.id || (columnKeys.skus.includes("ti") ? null : undefined),
    };
  });

  const productLineNames = new Set<string>();

  const productLines = data.productLines.reduce<ProductLineInput[]>(
    (acc, pl) => {
      productLineNames.add(pl.name);
      acc.push({ id: productLineIdsByName[pl.name], brandId, ...pl });
      return acc;
    },
    [],
  );

  const skuOnlyProductLineNames = new Set<string>();

  const productLinesForSkus = data.skus.reduce<ProductLineInput[]>(
    (acc, sku) => {
      const { productLineName } = sku;
      const shouldAdd =
        !productLineNames.has(productLineName) &&
        !existingProductLineIdsByName?.[productLineName] &&
        !skuOnlyProductLineNames.has(productLineName);

      if (shouldAdd) {
        skuOnlyProductLineNames.add(productLineName);
        acc.push({
          id: productLineIdsByName[productLineName],
          brandId,
          name: productLineName,
        });
      }

      return acc;
    },
    [],
  );

  return {
    productLines,
    productLinesForSkus,
    dimensions,
    tiHis,
    skus,
    skuComponents,
  };
}

type ChunkedImportResults = {
  productLines: NonNullable<
    NonNullable<
      ImportProductsData["upsertProductLinesByBrandIdAndName"]
    >["productLines"]
  >[number][];
  skus: NonNullable<
    NonNullable<ImportProductsData["upsertSkusById"]>["skus"]
  >[number][];
};

type UsePerformImportReturnTuple = [
  performImport: (
    data: ImportProductsVariables,
  ) => Promise<ChunkedImportResults>,
  context: { isLoading: boolean },
];

export function usePerformImport(): UsePerformImportReturnTuple {
  const client = useApolloClient();
  const [isLoading, setIsLoading] = useState(false);

  async function performImport(data: ImportProductsVariables) {
    setIsLoading(true);

    const chunkedVars = getChunkedTuple(
      [
        data.productLines,
        data.productLinesForSkus,
        data.dimensions,
        data.tiHis,
        data.skus,
        data.skuComponents,
      ],
      { maxByteSize: DEFAULT_MAX_BYTE_SIZE - 3500 },
    );

    try {
      const allResults = await chunkedVars.reduce(
        async (
          prev,
          [
            productLines,
            productLinesForSkus,
            dimensions,
            tiHis,
            skus,
            skuComponents,
          ],
        ) => {
          const prevData = await prev;

          const { data: chunkData } = await client.mutate({
            mutation: importMutation,
            variables: {
              productLines,
              productLinesForSkus,
              dimensions,
              tiHis,
              skus,
              skuComponents,
            },
          });

          return {
            productLines: [
              ...prevData.productLines,
              ...(chunkData?.upsertProductLinesByBrandIdAndName?.productLines ||
                []),
              ...(chunkData?.upsertProductLinesForSkus?.productLines || []),
            ],
            skus: [
              ...prevData.skus,
              ...(chunkData?.upsertSkusById?.skus || []),
            ],
          };
        },
        Promise.resolve<ChunkedImportResults>({
          productLines: [],
          skus: [],
        }),
      );

      const { cache } = client;
      cache.modify({
        fields: makeClearListFieldCache("productLines"),
      });
      cache.modify({ fields: makeClearListFieldCache("skus") });
      const clearNested = makeClearNestedListFieldCache(cache);
      clearNested("Brand", ["productLines"]);
      const clearType = makeClearCachedType(cache);
      clearType("ProductLine");
      clearType("Sku");
      clearType("SkuComponent");
      cache.gc();

      return allResults;
    } finally {
      setIsLoading(false);
    }
  }

  return [performImport, { isLoading }];
}
